/**
 * EN Messages Index
 *
 * CORE namespaces are imported synchronously — they land in the main bundle
 * and are available on first paint. Only namespaces needed on the landing
 * page, auth pages, and global UI (nav, footer, errors) belong here.
 *
 * Non-core namespaces are lazy-loaded via loadEnGroup(). Each call maps to
 * a named webpack chunk that is downloaded in parallel with the page chunk
 * that needs it (see App.tsx). This keeps ~468 KB of translation data out of
 * the main bundle, directly reducing main-thread parse time (TBT).
 *
 * CORE namespaces (~15 KB source):
 *   common, nav, auth, landing, footer, errors, validation, dateTime, banks
 *
 * Lazy groups:
 *   'marketing'  — learnMore, featuresPage, pricing, pricingPage, faqPage,
 *                  aboutPage, contactPage, blogPage, securityPage,
 *                  premiumSubmissionPage
 *   'blog'       — interestRatesBlog, delaysBlog, preApproveBlog,
 *                  appreciationBlog, bankCapitalBlog, dailyRentalsBlog
 *   'legal'      — privacy, terms, compliance, securityPolicy
 *   'app'        — dashboard, applications, documents, notifications,
 *                  settings, profile, documentsPage, documentUnderwriter
 *   'apply'      — creditAssessment, payment, secureData, confirmation,
 *                  application, documentRequests, coApplicant,
 *   'diligence'  — diligence
 *   'simulator'  — simulator
 */

// ─────────────────────────────────────────────────────────────────────────────
// CORE — synchronous, always in main bundle
// ─────────────────────────────────────────────────────────────────────────────
import { common }     from './common';
import { nav }        from './nav';
import { auth }       from './auth';
import { landing }    from './landing';
import { footer }     from './footer';
import { errors }     from './errors';
import { validation } from './validation';
import { dateTime }   from './dateTime';
import { banks }      from './banks';

export const coreEn = {
  common,
  nav,
  auth,
  landing,
  footer,
  errors,
  validation,
  dateTime,
  banks,
} as const;

export type CoreMessages = typeof coreEn;

// ─────────────────────────────────────────────────────────────────────────────
// LAZY GROUPS — each case becomes one webpack chunk via webpackChunkName.
// Multiple import() calls sharing the same chunk name are merged by webpack
// into a single network request.
// ─────────────────────────────────────────────────────────────────────────────

export type EnGroup =
  | 'marketing'
  | 'blog'
  | 'legal'
  | 'app'
  | 'apply'
  | 'diligence'
  | 'simulator'
  | 'fill';

export async function loadEnGroup(group: EnGroup): Promise<Record<string, unknown>> {
  switch (group) {
    case 'marketing': {
      const [
        { learnMore },
        { featuresPage },
        { pricing, pricingPage },
        { faqPage },
        { aboutPage, bookMeeting },
        { contactPage },
        { blogPage },
        { securityPage },
        { premiumSubmissionPage },
        { appraiserLocator },
        { whyNotBroker },
        { publicProfiles },
      ] = await Promise.all([
        import(/* webpackChunkName: "en-marketing" */ './learnMore'),
        import(/* webpackChunkName: "en-marketing" */ './features'),
        import(/* webpackChunkName: "en-marketing" */ './pricing'),
        import(/* webpackChunkName: "en-marketing" */ './faq'),
        import(/* webpackChunkName: "en-marketing" */ './about'),
        import(/* webpackChunkName: "en-marketing" */ './contact'),
        import(/* webpackChunkName: "en-marketing" */ './blog'),
        import(/* webpackChunkName: "en-marketing" */ './security'),
        import(/* webpackChunkName: "en-marketing" */ './premiumSubmission'),
        import(/* webpackChunkName: "en-marketing" */ './appraiserLocator'),
        import(/* webpackChunkName: "en-marketing" */ './whyNotBroker'),
        import(/* webpackChunkName: "en-marketing" */ './publicProfiles'),
      ]); 
      return {
        learnMore, featuresPage, pricing, pricingPage, faqPage,
        aboutPage, bookMeeting, contactPage, blogPage, securityPage, premiumSubmissionPage,
        appraiserLocator, whyNotBroker, publicProfiles,
      };
    }

    case 'blog': {
      const [
        { interestRatesBlog },
        { delaysBlog },
        { preApproveBlog },
        { appreciationBlog },
        { bankCapitalBlog },
        { dailyRentalsBlog },
        { blogProcess },
        { blogBankComparisons },
        { blogPropertyAppraisal },
        { blogExpat },
        { blogConfotur },
        { blogMarket },
        { blogScenarios },
        { blogPage },
        { drVsCanadaBlog },
        { drVsUsaBlog },
        { confoturZonesBlog },
        { backTaxesBlog },
        { cashVsMortgageBlog },
        { confoturPreconBlog },
        { bankAccountEarlyBlog },
        { jobLossPreconBlog },
        { financePreconBlog },
        { preconTimelineBlog },
        { blogEquityMortgage },
      ] = await Promise.all([
        import(/* webpackChunkName: "en-blog" */ './interestRatesBlog'),
        import(/* webpackChunkName: "en-blog" */ './delaysBlog'),
        import(/* webpackChunkName: "en-blog" */ './preApproveBlog'),
        import(/* webpackChunkName: "en-blog" */ './appreciationBlog'),
        import(/* webpackChunkName: "en-blog" */ './bankCapitalBlog'),
        import(/* webpackChunkName: "en-blog" */ './dailyRentalsBlog'),
        import(/* webpackChunkName: "en-blog" */ './blogProcess'),
        import(/* webpackChunkName: "en-blog" */ './blogBankComparisons'),
        import(/* webpackChunkName: "en-blog" */ './blogPropertyAppraisal'),
        import(/* webpackChunkName: "en-blog" */ './blogExpat'),
        import(/* webpackChunkName: "en-blog" */ './blogConfotur'),
        import(/* webpackChunkName: "en-blog" */ './blogMarket'),
        import(/* webpackChunkName: "en-blog" */ './blogScenarios'),
        import(/* webpackChunkName: "en-blog" */ './blog'),
        import(/* webpackChunkName: "en-blog" */ './drVsCanadaBlog'),
        import(/* webpackChunkName: "en-blog" */ './drVsUsaBlog'),
        import(/* webpackChunkName: "en-blog" */ './confoturZonesBlog'),
        import(/* webpackChunkName: "en-blog" */ './backTaxesBlog'),
        import(/* webpackChunkName: "en-blog" */ './cashVsMortgageBlog'),
        import(/* webpackChunkName: "en-blog" */ './confoturPreconBlog'),
        import(/* webpackChunkName: "en-blog" */ './bankAccountEarlyBlog'),
        import(/* webpackChunkName: "en-blog" */ './jobLossPreconBlog'),
        import(/* webpackChunkName: "en-blog" */ './financePreconBlog'),
        import(/* webpackChunkName: "en-blog" */ './preconTimelineBlog'),
        import(/* webpackChunkName: "en-blog" */ './blogEquityMortgage'),
      ]);
      return {
        interestRatesBlog, delaysBlog, preApproveBlog,
        appreciationBlog, bankCapitalBlog, dailyRentalsBlog,
        blogProcess, blogBankComparisons, blogPropertyAppraisal,
        blogExpat,
        blogConfotur,
        blogMarket,
        blogScenarios,
        blogPage,
        drVsCanadaBlog,
        drVsUsaBlog,
        confoturZonesBlog,
        backTaxesBlog,
        cashVsMortgageBlog, confoturPreconBlog, bankAccountEarlyBlog,
        jobLossPreconBlog, financePreconBlog, preconTimelineBlog,
        blogEquityMortgage,
      };
    }

    case 'legal': {
      const [
        { privacy },
        { terms },
        { compliance },
        { securityPolicy },
      ] = await Promise.all([
        import(/* webpackChunkName: "en-legal" */ './privacy'),
        import(/* webpackChunkName: "en-legal" */ './terms'),
        import(/* webpackChunkName: "en-legal" */ './compliance'),
        import(/* webpackChunkName: "en-legal" */ './securityPolicy'),
      ]);
      return { privacy, terms, compliance, securityPolicy };
    }

    case 'app': {
      const [
        { dashboard },
        { applications, applicationTypeConversion },
        { premium },
        { documents },
        { notifications },
        { settings },
        { profile },
        { documentsPage },
        { coApplicant },
        { documentUnderwriter },
        { actions },
        { ceilings },
        { controls },
        { header },
        { hero },
        { mailto },
        { skippedBanks },
        { holdingPeriod },
        { adminPreConstruction },
        { delegation },
      ] = await Promise.all([
        import(/* webpackChunkName: "en-app" */ './dashboard'),
        import(/* webpackChunkName: "en-app" */ './applications'),
        import(/* webpackChunkName: "en-app" */ './premium'),
        import(/* webpackChunkName: "en-app" */ './documents'),
        import(/* webpackChunkName: "en-app" */ './notifications'),
        import(/* webpackChunkName: "en-app" */ './settings'),
        import(/* webpackChunkName: "en-app" */ './profile'),
        import(/* webpackChunkName: "en-app" */ './documentsPage'),
        import(/* webpackChunkName: "en-app" */ './coApplicant'),
        import(/* webpackChunkName: "en-app" */ './documentUnderwriter'),
        import(/* webpackChunkName: "en-app" */ './actions'),
        import(/* webpackChunkName: "en-app" */ './ceilings'),
        import(/* webpackChunkName: "en-app" */ './controls'),
        import(/* webpackChunkName: "en-app" */ './header'),
        import(/* webpackChunkName: "en-app" */ './hero'),
        import(/* webpackChunkName: "en-app" */ './mailto'),
        import(/* webpackChunkName: "en-app" */ './skippedBanks'),
        import(/* webpackChunkName: "en-app" */ './holdingPeriod'),
        import(/* webpackChunkName: "en-app" */ './adminPreConstruction'),
        import(/* webpackChunkName: "en-app" */ './delegation'),
      ]);
      return {
        dashboard, applications, applicationTypeConversion, premium, documents, notifications,
        settings, profile, documentsPage, coApplicant, documentUnderwriter,
        actions, ceilings, controls, header, hero, mailto, skippedBanks,
        holdingPeriod, adminPreConstruction, delegation,
      };
    }

    case 'apply': {
      const [
        { creditAssessment },
        { payment },
        { secureData },
        { confirmation, application },
        { documentRequests },
        { coApplicant },
        { docDescriptions },
        { appraiserStep },
        { formSchema },
        { submit },
        { preConstruction },
        { equityMortgage },
      ] = await Promise.all([
        import(/* webpackChunkName: "en-apply" */ './creditAssessment'),
        import(/* webpackChunkName: "en-apply" */ './payment'),
        import(/* webpackChunkName: "en-apply" */ './secureData'),
        import(/* webpackChunkName: "en-apply" */ './confirmation'),
        import(/* webpackChunkName: "en-apply" */ './documentRequests'),
        import(/* webpackChunkName: "en-apply" */ './coApplicant'),
        import(/* webpackChunkName: "en-apply" */ './docDescriptions'),
        import(/* webpackChunkName: "en-apply" */ './appraiserStep'),
        import(/* webpackChunkName: "en-apply" */ './formSchema'),
        import(/* webpackChunkName: "en-apply" */ './submit'),
        import(/* webpackChunkName: "en-apply" */ './preConstruction'),
        import(/* webpackChunkName: "en-apply" */ './equityMortgage'),
      ]);
      return {
        creditAssessment, payment, secureData, confirmation, application,
        appraiserStep, docDescriptions, documentRequests, coApplicant, preConstruction,
        equityMortgage,
      };
    }

    case 'diligence': {
      const { diligence } = await import(/* webpackChunkName: "en-diligence" */ './diligence');
      return { diligence };
    }

    case 'simulator': {
      const { simulator } = await import(/* webpackChunkName: "en-simulator" */ './simulator');
      return { simulator };
    }
    case 'fill': {
      const { fill } = await import(/* webpackChunkName: "en-fill" */ './fill');
      return { fill };
    }
  }
}

export default coreEn;